import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, MessageCircle, FileText, Video, Calendar, Download } from 'lucide-react';
import { BackButton } from '@/components/ui/back-button';

export default function ResourcesPage() {
  const resources = [
    {
      title: "Forum Communautaire",
      description: "Échangez avec d'autres membres de la communauté HED, partagez vos expériences et posez vos questions.",
      icon: <MessageCircle className="w-8 h-8 text-purple-600" />,
      link: "/forum",
      buttonText: "Accéder au forum"
    },
    {
      title: "Articles & Guides",
      description: "Découvrez nos articles et guides pratiques pour approfondir votre compréhension du Human Design.",
      icon: <FileText className="w-8 h-8 text-blue-600" />,
      link: "#",
      buttonText: "Consulter les articles"
    },
    {
      title: "Webinaires & Formations",
      description: "Participez à nos webinaires et formations en ligne pour développer vos connaissances en HED.",
      icon: <Video className="w-8 h-8 text-green-600" />,
      link: "#",
      buttonText: "Voir le programme"
    }
  ];

  const latestArticles = [
    {
      title: "Comprendre les 5 types énergétiques du Human Design",
      category: "Fondamentaux",
      date: "15 mai 2025",
      excerpt: "Découvrez les caractéristiques des Manifestors, Generators, Manifesting Generators, Projectors et Reflectors."
    },
    {
      title: "Comment optimiser votre énergie selon votre type",
      category: "Conseils pratiques",
      date: "2 mai 2025",
      excerpt: "Des stratégies concrètes pour aligner vos actions avec votre design énergétique naturel."
    },
    {
      title: "Les centres énergétiques : guide complet",
      category: "Approfondissement",
      date: "28 avril 2025",
      excerpt: "Tout ce que vous devez savoir sur les 9 centres énergétiques et leur influence sur votre vie."
    }
  ];

  const upcomingEvents = [
    {
      title: "Webinaire : Introduction au Human Design",
      date: "15 juin 2025",
      time: "19h00 - 20h30",
      type: "En ligne"
    },
    {
      title: "Atelier pratique : Alignement énergétique",
      date: "22 juin 2025",
      time: "14h00 - 17h00",
      type: "Présentiel - Paris"
    },
    {
      title: "Formation : Devenir praticien HED",
      date: "5-9 juillet 2025",
      time: "9h00 - 17h00",
      type: "Présentiel - Boissy-le-Châtel"
    }
  ];

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-6xl mx-auto space-y-8">
        <BackButton to="/" />
        
        <div className="text-center space-y-4 mb-8">
          <div className="inline-block p-3 bg-white rounded-2xl shadow-xl mb-4">
            <BookOpen className="w-10 h-10 text-purple-600" />
          </div>
          <h1 className="text-3xl font-bold">Ressources & Communauté</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explorez nos ressources et rejoignez notre communauté pour approfondir votre compréhension du Human Design
          </p>
        </div>

        {/* Main Resources */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {resources.map((resource, index) => (
            <Card key={index} className="hover:shadow-lg transition-all">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="p-3 bg-purple-100 rounded-lg self-start mb-4">
                  {resource.icon}
                </div>
                <h2 className="text-xl font-bold mb-2">{resource.title}</h2>
                <p className="text-gray-600 mb-6 flex-grow">{resource.description}</p>
                <Button asChild className="self-start">
                  <Link to={resource.link}>
                    {resource.buttonText}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Latest Articles */}
        <Card className="bg-white/80 backdrop-blur-sm">
          <CardContent className="p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Articles Récents</h2>
              <Button variant="outline" className="gap-2">
                <FileText className="w-4 h-4" />
                Tous les articles
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {latestArticles.map((article, index) => (
                <div key={index} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                      {article.category}
                    </span>
                    <span className="text-xs text-gray-500">{article.date}</span>
                  </div>
                  <h3 className="font-semibold hover:text-purple-700 cursor-pointer">
                    {article.title}
                  </h3>
                  <p className="text-sm text-gray-600">{article.excerpt}</p>
                  <a href="#" className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1">
                    Lire l'article
                    <ArrowRight className="w-3 h-3" />
                  </a>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card className="bg-white/80 backdrop-blur-sm">
          <CardContent className="p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Événements à Venir</h2>
              <Button variant="outline" className="gap-2">
                <Calendar className="w-4 h-4" />
                Calendrier complet
              </Button>
            </div>
            
            <div className="space-y-4">
              {upcomingEvents.map((event, index) => (
                <div key={index} className="flex flex-col md:flex-row justify-between items-start md:items-center p-4 border rounded-lg hover:bg-gray-50">
                  <div className="space-y-1 mb-3 md:mb-0">
                    <h3 className="font-semibold">{event.title}</h3>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span>{event.date}</span>
                      <span>•</span>
                      <span>{event.time}</span>
                    </div>
                    <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {event.type}
                    </span>
                  </div>
                  <Button>S'inscrire</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Downloads */}
        <Card className="bg-gradient-to-r from-[#54075B] to-purple-700 text-white">
          <CardContent className="p-8 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="space-y-4 text-center md:text-left">
              <h2 className="text-2xl font-bold">Ressources Gratuites</h2>
              <p className="text-lg">
                Téléchargez notre guide d'introduction au Human Design et commencez votre voyage énergétique dès aujourd'hui.
              </p>
            </div>
            <Button variant="secondary" size="lg" className="gap-2 whitespace-nowrap">
              <Download className="w-5 h-5" />
              Télécharger le guide
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}